import normalize from "json-api-normalizer";
import get from "lodash/get";
import { push } from "connected-react-router";

import NotificationService from "../services/notification.service";
import TemplatesService from "../services/templates.service";

import { FETCH_SUCCESS, FETCH_FAILED, FETCH_START } from "../actions";
import { ASYNC_END, ASYNC_START } from "./types";
import { setResouceType } from "../reducers/resources.reducer";

const RESOURCE_TYPE = "templates";

export const fetchTemplates = (params = {}) => async (dispatch, getState) => {
  params.page = { number: get(params, "page.number", 1), size: 15 };
  params.include = "templates_features.feature,templates_bundles.bundle,templates_license_bundles.license_bundle,products";
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch({ type: FETCH_START, payload: { page: params.page.number } });
  try {
    const result = await TemplatesService.all(tenantSlug, params);
    const data = normalize(result.data, { endpoint: RESOURCE_TYPE });
    if (!data.templates) data.templates = [];

    dispatch({ type: FETCH_SUCCESS, payload: { ...data, page: params.page.number } });
  } catch (e) {
    dispatch({ type: FETCH_FAILED, payload: get(e, "response.data", {}) });

    NotificationService.addError(`Could not fetch templates. ${NotificationService.handleMessage(e)}`);
  }
};

export const fetchTemplate = id => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");

  dispatch(setResouceType(RESOURCE_TYPE));
  dispatch({ type: FETCH_START, payload: {} });
  try {
    const result = await TemplatesService.get(id, tenantSlug, {
      include: "templates_features.feature,templates_bundles.bundle,templates_license_bundles.license_bundle,products"
    });
    const data = normalize(result.data, { endpoint: RESOURCE_TYPE });

    dispatch({ type: FETCH_SUCCESS, payload: data });
  } catch (e) {
    dispatch({ type: FETCH_FAILED, payload: get(e, "response.data", {}) });

    NotificationService.addError(`Could not fetch template ${id}. ${NotificationService.handleMessage(e)}`);
  }
};

export const createTemplate = data => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");
  try {
    dispatch({ type: ASYNC_START, payload: { resource: RESOURCE_TYPE } });
    const createTemplateResponse = await TemplatesService.create(tenantSlug, data);

    const templateId = get(createTemplateResponse, "data.data.id");

    await Promise.all(
      data.templatesBundles
        .filter(tBundle => !tBundle.removed)
        .map(tBundle => TemplatesService.createBundleLink(tenantSlug, templateId, tBundle))
    );

    await Promise.all(
      data.templatesLicenseBundles
        .filter(tBundle => !tBundle.removed)
        .map(tBundle => TemplatesService.createLicenseBundleLink(tenantSlug, templateId, tBundle))
    );

    await Promise.all(
      data.templatesFeatures
        .filter(tFeature => !tFeature.removed)
        .map(tFeature => TemplatesService.createFeatureLink(tenantSlug, templateId, tFeature))
    );

    dispatch({ type: ASYNC_END });
    dispatch(push("/templates"));
    NotificationService.addSuccess("Template successfully created!");
  } catch (e) {
    dispatch({ type: ASYNC_END, payload: { success: false } });
    NotificationService.addError(`Failed creating the template! ${NotificationService.handleMessage(e)}`);
  }
};

export const updateTemplate = (id, data) => async (dispatch, getState) => {
  const tenantSlug = get(getState(), "common.currentTenant.slug");
  try {
    dispatch({ type: ASYNC_START, payload: { resource: RESOURCE_TYPE } });
    await TemplatesService.update(id, tenantSlug, data);

    await Promise.all(
      data.templatesFeatures.map(templatesFeature => {
        if (templatesFeature.id && templatesFeature.removed)
          return TemplatesService.removeFeatureLink(tenantSlug, id, templatesFeature.id);
        else if (templatesFeature.id && !templatesFeature.removed) {
          return TemplatesService.updateFeatureLink(tenantSlug, id, templatesFeature);
        } else if (!templatesFeature.id && !templatesFeature.removed)
          return TemplatesService.createFeatureLink(tenantSlug, id, templatesFeature);
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.templatesBundles.map(templatesBundle => {
        if (templatesBundle.id && templatesBundle.removed)
          return TemplatesService.removeBundleLink(tenantSlug, id, templatesBundle.id);
        else if (templatesBundle.id && !templatesBundle.removed) {
          return TemplatesService.updateBundleLink(tenantSlug, id, templatesBundle);
        } else if (!templatesBundle.id && !templatesBundle.removed)
          return TemplatesService.createBundleLink(tenantSlug, id, templatesBundle);
        return Promise.resolve(true);
      })
    );

    await Promise.all(
      data.templatesLicenseBundles.map(templatesLicenseBundle => {
        if (templatesLicenseBundle.id && templatesLicenseBundle.removed)
          return TemplatesService.removeLicenseBundleLink(tenantSlug, id, templatesLicenseBundle.id);
        else if (templatesLicenseBundle.id && !templatesLicenseBundle.removed) {
          return TemplatesService.updateLicenseBundleLink(tenantSlug, id, templatesLicenseBundle);
        } else if (!templatesLicenseBundle.id && !templatesLicenseBundle.removed)
          return TemplatesService.createLicenseBundleLink(tenantSlug, id, templatesLicenseBundle);
        return Promise.resolve(true);
      })
    );
    dispatch({ type: ASYNC_END });
    dispatch(push("/templates"));
    NotificationService.addSuccess("Template successfully updated!");
  } catch (e) {
    dispatch({ type: ASYNC_END, payload: { success: false } });
    NotificationService.addError(`Failed updating the template! ${NotificationService.handleMessage(e)}`);
  }
};
